import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppMutation } from "../../../../services";
import parentServices from "../../../../services/parent-services";
import { timings } from "../../../../utils/constants/appActions";
import moment from "moment/moment";
import Div from "@jumbo/shared/Div";
import {
    Avatar,
    FormControl,
    MenuItem,
    TextField,
    Box,
    Typography,
    Paper,
    ListSubheader,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import DDImage from "../../../../component/DDImage";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import IconButton from "@mui/material/IconButton";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import { fr } from "date-fns/locale";

const AddBookingByTeacher = ({
                                 availableDates: dates,
                                 type,
                                 pastDate,
                                 values,
                                 setValues,
                                 minDate,
                                 maxDate,
                                 setMaxDate,
                                 subscription,
                             }) => {
    const [availableDates, setAvailableDates] = useState(dates);
    const { t } = useTranslation();

    const [sTimings, setSTimings] = useState([]);
    const [open, setOpen] = React.useState(false);

    const [teacher, setTeacher] = React.useState();
    const [teachers, setTeachers] = React.useState([]);

    // ✅ favorites always visible + combined options shown in dropdown
    const [favOptions, setFavOptions] = useState([]); // favourites list
    const [options, setOptions] = useState([]); // merged list (favs + search results)
    const [inputValue, setInputValue] = useState("");

    const [prefetched, setPrefetched] = useState(false);
    const prefetchTeachers = () => {
        if (!values?.childId) return;
        if (prefetched) return;

        setPrefetched(true);

        // prefetch without search (backend returns isFavourite values)
        loadEvaluators({ search: "", mode: "prefetch" });
    };

    const {
        mutate: loadAvailableTeachersBySearch,
        isSuccess: isLATSuccess,
        isError: isLATError,
        data: latData,
    } = useAppMutation(parentServices.loadAvailableTeachersBySearch);

    const {
        mutate: loadAvailableTimesByTeacher,
        isSuccess: isLTSuccess,
        isLoading: isLTLoading,
        isError: isLTError,
        data: ltResult,
    } = useAppMutation(parentServices.loadAvailableTimesByTeacher);

    const {
        mutate: loadAvailableDatesByTeacher,
        isSuccess: isLDSuccess,
        isLoading: isLDLoading,
        isError: isLDError,
        data: ldResult,
    } = useAppMutation(parentServices.loadAvailableDatesByTeacher);

    const {
        mutate: favouriteTeacher,
        isSuccess: isFTSuccess,
        isError: isFTError,
        data: ftData,
    } = useAppMutation(parentServices.favouriteTeacher);

    // ✅ local favourite state (teacherId => boolean)
    const [favMap, setFavMap] = useState({});

    // ✅ helper: merge unique by teacherId
    const mergeUniqueByTeacherId = (a = [], b = []) => {
        const map = new Map();
        for (const x of a || []) if (x?.teacherId) map.set(x.teacherId, x);
        for (const x of b || []) if (x?.teacherId) map.set(x.teacherId, x);
        return Array.from(map.values());
    };

    // ✅ Sync favMap from server field "isFavourite" (do NOT use isFav)
    React.useEffect(() => {
        setFavMap((prev) => {
            const next = { ...prev };

            for (const o of favOptions || []) {
                const id = o?.teacherId;
                if (!id) continue;
                if (next[id] === undefined) next[id] = !!o.isFavourite;
            }

            for (const o of options || []) {
                const id = o?.teacherId;
                if (!id) continue;
                if (next[id] === undefined) next[id] = !!o.isFavourite;
            }

            return next;
        });
    }, [favOptions, options]);

    const isFav = (option) => {
        const id = option?.teacherId;
        if (!id) return false;
        return favMap[id] ?? !!option.isFavourite;
    };

    const toggleFav = (option) => {
        const childId = values?.childId;
        const teacherId = option?.teacherId;
        if (!childId || !teacherId) return;

        const nextFav = !isFav(option);

        // ✅ optimistic UI: map + lists
        setFavMap((prev) => ({ ...prev, [teacherId]: nextFav }));

        setFavOptions((prev) => {
            const map = new Map((prev || []).map((t) => [t.teacherId, t]));
            if (nextFav) map.set(teacherId, { ...option, isFavourite: true });
            else map.delete(teacherId);
            return Array.from(map.values());
        });

        setOptions((prev) =>
            (prev || []).map((t) =>
                t.teacherId === teacherId ? { ...t, isFavourite: nextFav } : t
            )
        );

        favouriteTeacher(
            {
                childId,
                teacherId,
                isFavourite: nextFav, // ✅ API expects this
            },
            {
                onError: () => {
                    // revert
                    setFavMap((prev) => ({ ...prev, [teacherId]: !nextFav }));

                    setFavOptions((prev) => {
                        const map = new Map((prev || []).map((t) => [t.teacherId, t]));
                        if (!nextFav) map.set(teacherId, { ...option, isFavourite: true });
                        else map.delete(teacherId);
                        return Array.from(map.values());
                    });

                    setOptions((prev) =>
                        (prev || []).map((t) =>
                            t.teacherId === teacherId ? { ...t, isFavourite: !nextFav } : t
                        )
                    );
                },
            }
        );
    };

    // ✅ When dates by teacher loaded
    React.useEffect(() => {
        if (isLDSuccess) {
            setAvailableDates(ldResult?.availableDates);
            setValues({ ...values, startTime: undefined });
            setSTimings([]);
            loadAvailableTimesByTeacher({
                date: moment(values.sstartDate).format("YYYY-MM-DD"),
                childId: values.childId,
                teacherId: values.teacherId,
                type,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLDSuccess]);

    // ✅ If you ever call loadAvailableTeachersBySearch without typing (some flows do),
    // keep favourites from response so they show immediately on open.
    React.useEffect(() => {
        if (isLATSuccess) {
            const list = latData?.availableTeachers || [];
            setTeachers(list);

            const favs = list.filter((x) => !!x?.isFavourite);
            setFavOptions((prev) => mergeUniqueByTeacherId(prev, favs));

            const merged = mergeUniqueByTeacherId(favs, list);
            setOptions(merged);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLATSuccess]);

    // ✅ Time slots effect (unchanged)
    React.useEffect(() => {
        if (isLTSuccess && ltResult) {
            const ls = [];
            for (const t of ltResult?.timings) {
                let date = new Date(`${values.startDate}T${t}`);
                const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                date = new Date(date.getTime() - userTimezoneOffset);
                if (date > new Date()) {
                    const expiredDate = new Date(subscription.expiredDate);
                    expiredDate.setMinutes(expiredDate.getMinutes() - 30);
                    if (date <= expiredDate) {
                        const v = timings.find(
                            (item) => item.value === moment(date).format("HH:mm")
                        );
                        if (v) ls.push(v);
                    }
                }
            }
            setSTimings(ls);
        } else {
            setSTimings([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLTError, isLTSuccess]);

    const handleChange = (prop) => (event) => {
        setValues({ ...values, [prop]: event.target.value });
    };

    // ✅ SEARCH: merges favourites + results, favourites always remain visible
    const loadEvaluators = ({ search = "", mode = "search" }) => {
        if (!values.childId) return;

        const selectedStartDateTime = new Date(minDate);
        const selectedEndDateTime = new Date(maxDate);

        const obj = {
            type,
            childId: values.childId,
            startDate: selectedStartDateTime,
            endDate: selectedEndDateTime,
            search, // "" for prefetch
        };

        loadAvailableTeachersBySearch(obj, {
            onSuccess: (data) => {
                const list = data?.availableTeachers || [];
                const favsFromApi = list.filter((x) => !!x?.isFavourite);

                // ✅ always update favourites cache
                setFavOptions((prevFavs) => {
                    const nextFavs = mergeUniqueByTeacherId(prevFavs, favsFromApi);

                    // ✅ KEY BEHAVIOR:
                    // - prefetch: show ONLY favourites
                    // - search: show favourites + results
                    if (mode === "prefetch") {
                        setOptions(nextFavs);
                    } else {
                        setOptions(mergeUniqueByTeacherId(nextFavs, list));
                    }

                    return nextFavs;
                });
            },
            onError: () => {
                // fallback: show favourites only
                setOptions(favOptions || []);
            },
        });
    };

    return (
        <Div>
            <FormControl
                fullWidth
                sx={{
                    width: { xs: "100%", xl: "100%", sm: "100%", md: "100%" },
                    mt: 3,
                }}
            >
                {/* TEACHER HEADER */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
                    <Box
                        component="span"
                        sx={{
                            fontSize: 20,
                            lineHeight: 1,
                            flexShrink: 0,
                        }}
                    >
                        👨‍🏫
                    </Box>

                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 700,
                            fontFamily: "'Nunito', sans-serif",
                            letterSpacing: "0.5px",
                            color: "#002465",
                            textTransform: "uppercase",
                            lineHeight: 1,
                        }}
                    >
                        {t("parent.title.teacher")}
                    </Typography>
                </Box>

                <Autocomplete
                    disableClearable
                    openOnFocus

                    // ✅ When opening without typing, show favourites immediately
                    onOpen={() => {
                        prefetchTeachers();            // ✅ hits API immediately
                        setOptions(favOptions || []);  // ✅ show ONLY favourites for now
                    }}
                    onFocus={() => {
                        prefetchTeachers();
                        setOptions(favOptions || []);
                    }}

                    options={[...(options || [])].sort((a, b) => {
                        // ✅ favourites first (uses isFavourite)
                        const af = (favMap[a?.teacherId] ?? !!a?.isFavourite) ? 1 : 0;
                        const bf = (favMap[b?.teacherId] ?? !!b?.isFavourite) ? 1 : 0;
                        if (af !== bf) return bf - af;

                        const an = `${a?.firstName || ""} ${a?.lastName || ""}`
                            .trim()
                            .toLowerCase();
                        const bn = `${b?.firstName || ""} ${b?.lastName || ""}`
                            .trim()
                            .toLowerCase();
                        return an.localeCompare(bn);
                    })}
                    popupIcon={<KeyboardArrowDownRoundedIcon />}
                    forcePopupIcon
                    groupBy={(option) =>
                        isFav(option) ? t("parent.title.favouriteTeachers") : t("parent.title.searchResult")
                    }
                    getOptionLabel={(option) =>
                        `${option.firstName || ""} ${option.lastName || ""}`.trim()
                    }
                    isOptionEqualToValue={(opt, val) => opt.teacherId === val.teacherId}
                    noOptionsText={
                        inputValue.length >= 2
                            ? t("parent.title.noTeacherAvailable")
                            : t("parent.title.searchAndTab")
                    }
                    onChange={(event, newValue) => {
                        setValues({
                            ...values,
                            teacherId: newValue?.teacherId || "",
                            startTime: undefined,
                        });
                        setTeacher(newValue);

                        if (newValue?.teacherId) {
                            loadAvailableDatesByTeacher({
                                startDate: minDate,
                                endDate: maxDate,
                                type,
                                teacherId: newValue.teacherId,
                            });
                        }
                    }}
                    onInputChange={(e, newInput) => {
                        setInputValue(newInput);

                        if (!newInput || newInput.length < 2) {
                            setOptions(favOptions || []);      // ✅ only favourites
                            return;
                        }

                        loadEvaluators({ search: newInput, mode: "search" }); // ✅ show favs + results
                    }}
                    PaperComponent={(props) => (
                        <Paper
                            {...props}
                            elevation={0}
                            sx={{
                                mt: 1,
                                borderRadius: 2,
                                border: "2px solid #2d6bbf",
                                overflow: "hidden",
                            }}
                        />
                    )}
                    renderGroup={(params) => {
                        if (!params.group) return <li key={params.key}>{params.children}</li>;

                        return (
                            <li key={params.key}>
                                <ListSubheader
                                    component="div"
                                    sx={{
                                        py: 1,
                                        px: 1.5,
                                        fontWeight: 800,
                                        fontSize: 12,
                                        letterSpacing: "0.12em",
                                        textTransform: "uppercase",
                                        color: "#6B7280",
                                        bgcolor: "#fff",
                                        borderBottom: "1px solid #EEF1F5",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {params.group}
                                </ListSubheader>

                                <Box sx={{ "& li": { px: 1.5 } }}>{params.children}</Box>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={t("parent.title.enterTeacherName") || "Type teacher name"}
                            variant="outlined"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "#fff",
                                    minHeight: 56,

                                    "& fieldset": {  borderWidth: 1 },
                                    "&:hover fieldset": { borderColor: "#2d6bbf" },
                                    "&.Mui-focused fieldset": { borderColor: "#2d6bbf" },

                                    paddingRight: 0,
                                },

                                "& .MuiAutocomplete-endAdornment": {
                                    position: "absolute",
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                },

                                "& .MuiAutocomplete-popupIndicator": {
                                    marginRight: 0,
                                },

                                "& .MuiOutlinedInput-input": {
                                    paddingRight: "42px",
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        const fav = isFav(option);

                        return (
                            <Box
                                component="li"
                                {...props}
                                key={option.teacherId}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.2,
                                    py: 1.2,
                                    borderBottom: "1px solid #EEF1F5",
                                    "&:last-of-type": { borderBottom: "none" },
                                }}
                            >
                                {/* Left: avatar + name */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                                    <Box sx={{ width: 34, height: 34, flexShrink: 0 }}>
                                        {option.images?.length > 0 ? (
                                            <DDImage
                                                path={option.images[0]}
                                                alt={`${option.firstName} ${option.lastName}`}
                                            />
                                        ) : (
                                            <Avatar
                                                sx={{ width: 34, height: 34 }}
                                                alt={`${option.firstName} ${option.lastName}`}
                                            />
                                        )}
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 14,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {option.firstName} {option.lastName}
                                    </Typography>
                                </Box>

                                {/* Right: favorite heart */}
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleFav(option);
                                    }}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "999px",
                                        //border: "1px solid #F9A8D4",
                                        //backgroundColor: fav ? "#fff" : "#F9A8D4",
                                        "&:hover": { backgroundColor: fav ? "#fff" : "#F9A8D4" },
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={
                                            fav
                                                ? "/images/icons/pink.png"
                                                : "/images/icons/white.png"
                                        }
                                        alt="favorite"
                                        sx={{
                                            width: 22,
                                            height: 22,
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        );
                    }}
                />
            </FormControl>

            {/* DATE + TIME UI (unchanged) */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                    mt: 3,
                }}
            >
                {/* DATE */}
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Box
                            component="span"
                            sx={{
                                fontSize: 18,
                                lineHeight: 1,
                                flexShrink: 0,
                                display: "inline-flex",
                                alignItems: "center",
                            }}
                        >
                            📅
                        </Box>

                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 700,
                                fontFamily: "'Nunito', sans-serif",
                                letterSpacing: "0.5px",
                                color: "#002465",
                                textTransform: "uppercase",
                                lineHeight: 1,
                        }}>
                            {t("common.date")?.toUpperCase?.() || "DATE"}
                        </Typography>
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                        <Stack spacing={0}>
                            {availableDates && availableDates.length > -1 && (
                                <DesktopDatePicker
                                    disabled={pastDate || !values?.childId}
                                    open={open}
                                    onOpen={() => setOpen(true)}
                                    onClose={() => setOpen(false)}
                                    shouldDisableDate={(day) => {
                                        if (pastDate) return false;

                                        if (
                                            availableDates &&
                                            availableDates.indexOf(moment(day).format("YYYY-MM-DD")) > -1
                                        ) {
                                            const currentDate = new Date();
                                            if (
                                                moment(currentDate).format("YYYY-MM-DD") ===
                                                moment(day).format("YYYY-MM-DD")
                                            ) {
                                                return true;
                                            }
                                            return false;
                                        }
                                        return true;
                                    }}
                                    minDate={pastDate ? null : minDate}
                                    maxDate={pastDate ? null : maxDate}
                                    loading={false}
                                    label={""}
                                    format="dd/MM/yyyy"
                                    value={values.sstartDate}
                                    onChange={(event) => {
                                        const startDate = moment(event).format("YYYY-MM-DD");
                                        const sstartDate = event;

                                        setValues({
                                            ...values,
                                            startDate,
                                            sstartDate,
                                            startTime: undefined,
                                        });

                                        setMaxDate(null);

                                        loadAvailableTimesByTeacher({
                                            date: startDate,
                                            childId: values.childId,
                                            teacherId: values.teacherId,
                                            type,
                                        });
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined",
                                            InputLabelProps: { shrink: false },
                                            sx: {
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    backgroundColor: "#fff",
                                                    minHeight: 56,
                                                    "& fieldset": { borderColor: "#D1D5DB" },
                                                    "&:hover fieldset": { borderColor: "#9CA3AF" },
                                                    "&.Mui-focused fieldset": { borderColor: "#9CA3AF" },
                                                },
                                                "& input": { fontWeight: 600 },
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    </LocalizationProvider>
                </Box>

                {/* TIME */}
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Box
                            component="span"
                            sx={{
                                fontSize: 18,
                                lineHeight: 1,
                                flexShrink: 0,
                                display: "inline-flex",
                                alignItems: "center",
                            }}
                        >
                            🕐
                        </Box>

                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 700,
                                fontFamily: "'Nunito', sans-serif",
                                letterSpacing: "0.5px",
                                color: "#002465",
                                textTransform: "uppercase",
                                lineHeight: 1,
                        }}>
                            {t("parent.title.time")?.toUpperCase?.() || "TIME"}
                        </Typography>
                    </Box>

                    <FormControl fullWidth>
                        <Select
                            disabled={!values?.childId || !values?.startDate}
                            value={values.startTime || ""}
                            onChange={handleChange("startTime")}
                            displayEmpty
                            IconComponent={ArrowDropDownIcon}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: "#fff",
                                minHeight: 56,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D5DB" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
                                "& .MuiSelect-select": {
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                },
                            }}
                        >
                            {sTimings.map((item, index) => (
                                <MenuItem key={"key-timing-" + index} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </Div>
    );
};

export default AddBookingByTeacher;